<script setup lang="ts">
import { TrendingDown, TrendingUp } from 'lucide-vue-next';
import { RAMP_GLYPHS, insertRampWords, type QualifierPart } from '@/utils/customPlaylistSchedule';

/**
 * A schedule's qualifier, "every 8 ↗ 3 tracks · ramp 7d", with an insert's
 * ramp drawn as a trend arrow instead of the text glyph. The arrow points up
 * when the insert plays more often and down when it plays less often.
 *
 * The glyph is still in the text, hidden from sight and from screen readers,
 * so copying the line or searching the page finds "every 8 ↗ 3 tracks", the
 * same text as the tooltip. A screen reader reads the arrow's label instead.
 *
 * The root is one span, so a caller's class and test id land on it.
 */
defineProps<{ parts: QualifierPart[] }>();

const SPOKEN: Readonly<Record<'up' | 'down', string>> = {
    up: 'more often, ramping to',
    down: 'less often, ramping to',
};
</script>

<template>
    <span>
        <template v-for="(part, index) in parts" :key="index">
            <template v-if="index > 0">{{ ' · ' }}</template>
            <span v-if="part.ramp" class="whitespace-nowrap" data-testid="schedule-ramp" :data-direction="part.ramp.direction"
                >{{ insertRampWords(part.ramp).before }}
                <component
                    :is="part.ramp.direction === 'up' ? TrendingUp : TrendingDown"
                    class="inline size-3.5 align-[-3px]"
                    role="img"
                    :aria-label="SPOKEN[part.ramp.direction]"
                /><span class="sr-only" aria-hidden="true">{{ RAMP_GLYPHS[part.ramp.direction] }}</span>
                {{ insertRampWords(part.ramp).after }}</span
            >
            <template v-else>{{ part.text }}</template>
        </template>
    </span>
</template>
